// SUB-OFFICE 7 — the eye-lamps. One InstancedMesh of gimbal heads plus one of
// emissive lens discs. Every head tracks the avatar in quantized 7.5-degree
// servo steps at 20 steps/s, staggered per lamp, with a one-frame overshoot —
// mechanisms do not ease. Iris state is a 3-frame texture atlas (shared).
// frustumCulled = false: the single-bounding-sphere bug is pre-empted.

import {
  CircleGeometry, Color, CylinderGeometry, DynamicDrawUsage, InstancedMesh,
  Matrix4, MeshBasicMaterial, MeshLambertMaterial, Object3D,
} from 'three';
import { PAL } from '../palette';
import { LAMPS, LAMP_Y } from '../world/layout';
import { makeIrisAtlas } from '../render/textures';
import { patchMaterial } from '../render/scene';
import type { Entity, EventBus } from './entities';
import type { Player } from './player';

const STEP = (7.5 * Math.PI) / 180;
const STEP_RATE = 20; // steps per second
const OVERSHOOT = 0.035; // one-frame servo overshoot, radians

export type IrisState = 0 | 1 | 2; // DILATED / NOMINAL / NARROW

export class EyeLamps implements Entity {
  readonly id = 'lamps';
  readonly heads: InstancedMesh;
  readonly lenses: InstancedMesh;

  private yaw: Float32Array;
  private displayYaw: Float32Array;
  private stepTimer: Float32Array;
  private overshootLeft: Float32Array;
  private dummy = new Object3D();
  private mat4 = new Matrix4();
  private lensMat: MeshBasicMaterial;
  // per-lamp downward pitch: the heads lean at the petitioner. Convergence
  // ripples nearest-first (a wave of attention closing in), releases together.
  private tilt: Float32Array;
  private tiltDelay: Float32Array;
  private tiltTarget = 0;
  private converging = false;

  constructor(private bus: EventBus, private player: Player) {
    const n = LAMPS.length;
    const headGeo = new CylinderGeometry(0.16, 0.24, 0.34, 10, 1);
    headGeo.rotateX(Math.PI / 2.4); // fixed downward pitch; yaw is the servo axis
    const headMat = new MeshLambertMaterial({ color: new Color(PAL.carbon).convertSRGBToLinear(), flatShading: true });
    patchMaterial(headMat);
    this.heads = new InstancedMesh(headGeo, headMat, n);
    this.heads.instanceMatrix.setUsage(DynamicDrawUsage);
    this.heads.frustumCulled = false;

    const lensGeo = new CircleGeometry(0.13, 10);
    lensGeo.rotateX(Math.PI / 2.4);
    lensGeo.translate(0, -0.19, 0.05);
    const atlas = makeIrisAtlas();
    atlas.repeat.x = 1 / 3;
    this.lensMat = new MeshBasicMaterial({ map: atlas });
    this.lenses = new InstancedMesh(lensGeo, this.lensMat, n);
    this.lenses.instanceMatrix.setUsage(DynamicDrawUsage);
    this.lenses.frustumCulled = false;

    this.yaw = new Float32Array(n);
    this.displayYaw = new Float32Array(n);
    this.stepTimer = new Float32Array(n);
    this.overshootLeft = new Float32Array(n);
    this.tilt = new Float32Array(n);
    this.tiltDelay = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      this.yaw[i] = Math.random() * Math.PI * 2;
      this.displayYaw[i] = this.yaw[i];
      this.stepTimer[i] = i * 0.013; // stagger: never a single mechanical chorus
    }
    this.writeAll();
  }

  /** Shared iris state (per-lamp iris arrives with the ceremony slice). */
  setIris(state: IrisState): void {
    const map = this.lensMat.map;
    if (map) map.offset.x = state / 3;
  }

  /** Adjudication: every head visibly tilts DOWN at the petitioner (the FP
   *  ceremony reads this from below; the long lens reads it as a shiver). */
  setConverge(on: boolean): void {
    this.tiltTarget = on ? 0.38 : 0;
    this.converging = on;
    if (on) {
      // stagger the lean by distance to the petitioner: the nearest heads turn
      // down first, the far ones a beat later — attention closing in, not a
      // single mechanical chorus. Release (off) drops the delays: light snaps.
      const px = this.player.pos.x, pz = this.player.pos.z;
      let maxD = 1;
      for (let i = 0; i < LAMPS.length; i++) {
        const dx = px - LAMPS[i][0], dz = pz - LAMPS[i][1];
        maxD = Math.max(maxD, Math.hypot(dx, dz));
      }
      for (let i = 0; i < LAMPS.length; i++) {
        const dx = px - LAMPS[i][0], dz = pz - LAMPS[i][1];
        this.tiltDelay[i] = (Math.hypot(dx, dz) / maxD) * 0.55; // up to 0.55s spread
      }
    } else {
      this.tiltDelay.fill(0);
    }
  }

  update(dt: number): void {
    // per-lamp tilt easing (staggered on converge, unified on release)
    {
      let tiltDirty = false;
      const rate = dt * 2.0;
      for (let i = 0; i < LAMPS.length; i++) {
        if (this.tiltDelay[i] > 0) {
          this.tiltDelay[i] = Math.max(0, this.tiltDelay[i] - dt);
          continue;
        }
        if (this.tilt[i] !== this.tiltTarget) {
          this.tilt[i] = Math.abs(this.tiltTarget - this.tilt[i]) <= rate
            ? this.tiltTarget
            : this.tilt[i] + Math.sign(this.tiltTarget - this.tilt[i]) * rate;
          tiltDirty = true;
        }
      }
      void this.converging;
      if (tiltDirty) this.writeAll();
    }
    const px = this.player.pos.x;
    const pz = this.player.pos.z;
    let dirty = false;
    for (let i = 0; i < LAMPS.length; i++) {
      const [lx, lz] = LAMPS[i];
      const target = Math.atan2(px - lx, pz - lz);
      // settle any overshoot from the previous frame
      if (this.overshootLeft[i] !== 0) {
        this.displayYaw[i] -= this.overshootLeft[i];
        this.overshootLeft[i] = 0;
        dirty = true;
      }
      let diff = target - this.yaw[i];
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      if (Math.abs(diff) >= STEP / 2) {
        this.stepTimer[i] += dt;
        const interval = 1 / STEP_RATE;
        while (this.stepTimer[i] >= interval && Math.abs(diff) >= STEP / 2) {
          this.stepTimer[i] -= interval;
          const s = Math.sign(diff) * STEP;
          this.yaw[i] += s;
          diff -= s;
          const over = Math.sign(s) * OVERSHOOT;
          this.displayYaw[i] = this.yaw[i] + over;
          this.overshootLeft[i] = over;
          dirty = true;
          // voice the servo only for nearby lamps (audio is additive, never noise)
          const d2 = (px - lx) * (px - lx) + (pz - lz) * (pz - lz);
          if (d2 < 120) this.bus.emit('audio:servo');
        }
      } else {
        this.stepTimer[i] = Math.min(this.stepTimer[i], 1 / STEP_RATE);
      }
    }
    if (dirty) this.writeAll();
  }

  private writeAll(): void {
    for (let i = 0; i < LAMPS.length; i++) {
      const [lx, lz] = LAMPS[i];
      this.dummy.position.set(lx, LAMP_Y, lz);
      this.dummy.rotation.order = 'YXZ'; // yaw first, then the converge tilt
      this.dummy.rotation.set(this.tilt[i], this.displayYaw[i], 0);
      this.dummy.updateMatrix();
      this.mat4.copy(this.dummy.matrix);
      this.heads.setMatrixAt(i, this.mat4);
      this.lenses.setMatrixAt(i, this.mat4);
    }
    this.heads.instanceMatrix.needsUpdate = true;
    this.lenses.instanceMatrix.needsUpdate = true;
  }
}
